import { useState, useEffect, useRef } from 'react'
import { Skeleton } from '@mantine/core'
import { XClose } from '@untitled-ui/icons-react'

import useComments from '@/hooks/useComments'
import useProfiles from '@/hooks/useProfiles'

import CommentItem from './CommentItem'
import CommentComposer from './CommentComposer'

export default function CommentsList({
    commentable_type,
    commentable_id,
    onClose,
}) {
    const [newComment, setNewComment] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const listRef = useRef(null)

    const { comments, isLoading, addComment, updateComment, deleteComment } = useComments(commentable_type, commentable_id)
    const { profiles, profilesMap } = useProfiles()

    // Scroll to bottom when comments change
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
        }
    }, [comments])

    const saveComment = async () => {
        if (!newComment.trim()) return

        setIsSaving(true)

        try {
            await addComment(newComment)
            setNewComment('')
        } catch {
            // Error handled by useAxiosApi
        }

        setIsSaving(false)
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-5 h-16 shrink-0 border-b border-gray-200/60">
                <h2 className="text-base font-medium text-gray-900">
                    Commentaires
                </h2>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                    <XClose width={20} height={20} />
                </button>
            </div>

            {/* Comments list */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-5">
                {isLoading && (
                    <div className="space-y-5">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <Skeleton circle height={32} width={32} className="shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton height={12} width="40%" radius="xl" />
                                    <Skeleton height={12} width={i === 1 ? '90%' : '70%'} radius="xl" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!isLoading && comments.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <span className="text-sm text-gray-400">
                            Aucun commentaire pour le moment
                        </span>
                        <span className="text-sm text-gray-300 mt-1">
                            Tapez @ pour mentionner quelqu'un
                        </span>
                    </div>
                )}

                {!isLoading && (
                    <div className="flex flex-col gap-1">
                        {comments.map((comment) => (
                            <CommentItem
                                comment={comment}
                                key={comment.id}
                                profilesMap={profilesMap}
                                profiles={profiles}
                                updateComment={updateComment}
                                deleteComment={deleteComment}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Compose area */}
            <div className="shrink-0 px-5 py-3">
                <CommentComposer
                    value={newComment}
                    onChange={setNewComment}
                    onSubmit={saveComment}
                    profiles={profiles}
                    profilesMap={profilesMap}
                    isSaving={isSaving}
                />
            </div>
        </div>
    )
}
